import { C, F, M, fmtTimeInTz } from './theme';
import { useIsMobile } from '../../lib/useIsMobile';
import { renderMaintConfirmation } from '../../lib/ops-board/maintConfirmation';
import type { Aircraft, Base, Booking, BookingKind, Staff, Student } from '../../lib/opsBoardTypes';

function ddMMM(iso: string): string {
  const d = new Date(iso);
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getUTCMonth()];
  return `${String(d.getUTCDate()).padStart(2, '0')} ${mon}`;
}

// Serviceability badge for an aircraft — one source of truth shared by the mobile Aircraft
// overview tab. Mirrors the derivation in MaintenanceSummary below (serviceable / degraded /
// U-S, honouring the future-dated "not yet commenced" guard); keep the two in sync. `today` is the
// board's VIEWED day (anchor tz) — the badge follows the day on screen, matching the row hatch.
export interface AircraftStatusInfo { kind: 'serviceable' | 'degraded' | 'us'; label: string; color: string; bg: string; note?: string }
export function aircraftStatus(a: Aircraft, today: string): AircraftStatusInfo {
  if (a.serviceable === false) {
    // A future-dated maintenance flips serviceable=false eagerly — don't show U/S until it commences (on the viewed day).
    const notCommenced = !!a.unserviceable_from && a.unserviceable_from.slice(0, 10) > today;
    if (!notCommenced) {
      const ecd = a.expected_return_date ? `ECD ${ddMMM(a.expected_return_date.slice(0, 10))}` : 'UFN';
      return { kind: 'us', label: `U/S · ${ecd}`, color: C.red, bg: C.redLight };
    }
  }
  if (a.capability_current && a.capability_current !== a.capability_normal) {
    return { kind: 'degraded', label: 'degraded', color: C.purple, bg: '#f5f3ff', note: a.capability_current };
  }
  return { kind: 'serviceable', label: 'serviceable', color: C.green, bg: C.greenLight };
}

// ─── Outside-hours drill-down popup (Step 5c.2.1) ────────────────────────────
interface OHProps {
  bookings: Booking[]; // outside-hours bookings to list
  staff: Map<string, Staff>;
  students: Map<string, Student>;
  bases: Map<string, Base>;
  allBases: boolean;
  onPick: (b: Booking) => void;
  onClose: () => void;
}

export function OutsideHoursPopup({ bookings, staff, students, bases, allBases, onPick, onClose }: OHProps) {
  const isMobile = useIsMobile();
  const line = (b: Booking): string => {
    const t = b.block_start_at ? fmtTimeInTz(b.block_start_at, b.dep_timezone).replace(':', '') : '----';
    const reg = b.asset_allocations.find((a) => a.aircraft_registration)?.aircraft_registration ?? '—';
    const instr = b.pilot_id ? staff.get(b.pilot_id) : null;
    const stu = b.trainee_id ? students.get(b.trainee_id) : null;
    const who = [instr?.pilot_code ?? instr?.preferred_name ?? instr?.full_name, stu?.pilot_code ?? stu?.preferred_name ?? stu?.full_name ?? b.trainee?.preferred_name ?? b.trainee?.full_name].filter(Boolean).join('/') || '—';
    return `${t} ${reg} ${who}`;
  };

  // Group by base in all-bases mode.
  const groups = new Map<string, Booking[]>();
  for (const b of bookings) {
    const key = allBases ? (bases.get(b.base_id)?.name ?? b.base_id) : '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(b);
  }

  return (
    <div style={{ position: 'fixed', top: 96, zIndex: 210, fontFamily: F, overflow: 'hidden', background: C.surface, border: `1px solid ${C.amberBorder}`, borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.16)', ...(isMobile ? { left: 8, right: 8, width: 'auto' } : { right: 24, width: 300 }) }}>
      <div style={{ background: '#92400e', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>⏰ Outside hours ({bookings.length})</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 16 }}>✕</button>
      </div>
      <div style={{ padding: '8px 10px', maxHeight: 320, overflowY: 'auto' }}>
        {bookings.length === 0 && <div style={{ fontSize: 11, color: C.textMuted, padding: 6 }}>None.</div>}
        {[...groups.entries()].map(([g, list]) => (
          <div key={g} style={{ marginBottom: g ? 8 : 0 }}>
            {g && <div style={{ fontSize: 9, fontWeight: 700, color: C.textMuted, letterSpacing: '0.06em', margin: '4px 2px' }}>{g.toUpperCase()}</div>}
            {list.map((b) => (
              <button key={b.id} onClick={() => onPick(b)} style={{ display: 'block', width: '100%', textAlign: 'left', background: C.amberLight, border: `1px solid ${C.amberBorder}`, borderRadius: 5, padding: '5px 8px', marginBottom: 4, fontSize: 11, color: C.amber, fontFamily: M, cursor: 'pointer' }}>
                {line(b)}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Waitlist drill-down popup (Step 5c.3 §B) ────────────────────────────────
export function WaitlistPopup({ bookings, staff, students, bases, allBases, onPick, onClose }: OHProps) {
  const isMobile = useIsMobile();
  const line = (b: Booking): string => {
    const t = b.block_start_at ? fmtTimeInTz(b.block_start_at, b.dep_timezone).replace(':', '') : '----';
    const reg = b.asset_allocations.find((a) => a.aircraft_registration)?.aircraft_registration ?? '—';
    const crew = b.participants.find((p) => p.role === 'instructor');
    const stu = b.participants.find((p) => p.role === 'student');
    const nm = (uid: string | null | undefined, embedded: { full_name: string; preferred_name: string | null; pilot_code?: string | null } | null | undefined) => {
      if (embedded) return embedded.pilot_code ?? embedded.preferred_name ?? embedded.full_name;
      if (!uid) return undefined;
      const s = staff.get(uid) ?? students.get(uid);
      return s ? (s.pilot_code ?? s.preferred_name ?? s.full_name) : undefined;
    };
    const who = [nm(crew?.user_id, crew?.user), nm(stu?.user_id, stu?.user)].filter(Boolean).join('/') || '—';
    return `${t} ${reg} ${who}`;
  };

  const groups = new Map<string, Booking[]>();
  for (const b of bookings) {
    const key = allBases ? (bases.get(b.base_id)?.name ?? b.base_id) : '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(b);
  }

  return (
    <div style={{ position: 'fixed', top: 96, zIndex: 210, fontFamily: F, overflow: 'hidden', background: C.surface, border: `1px solid ${C.amberBorder}`, borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.16)', ...(isMobile ? { left: 8, right: 8, width: 'auto' } : { right: 24, width: 300 }) }}>
      <div style={{ background: C.amber, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>⏳ Waitlist — unallocated ({bookings.length})</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', cursor: 'pointer', fontSize: 16 }}>✕</button>
      </div>
      <div style={{ padding: '8px 10px', maxHeight: 320, overflowY: 'auto' }}>
        {bookings.length === 0 && <div style={{ fontSize: 11, color: C.textMuted, padding: 6 }}>None.</div>}
        {[...groups.entries()].map(([g, list]) => (
          <div key={g} style={{ marginBottom: g ? 8 : 0 }}>
            {g && <div style={{ fontSize: 9, fontWeight: 700, color: C.textMuted, letterSpacing: '0.06em', margin: '4px 2px' }}>{g.toUpperCase()}</div>}
            {list.map((b) => (
              <button key={b.id} onClick={() => onPick(b)} style={{ display: 'block', width: '100%', textAlign: 'left', background: C.amberLight, border: `1px solid ${C.amberBorder}`, borderRadius: 5, padding: '5px 8px', marginBottom: 4, fontSize: 11, color: C.amber, fontFamily: M, cursor: 'pointer' }}>
                {line(b)}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Maintenance summary panel (Step 5c.2.1 — restores the prototype panel) ──
interface MSProps {
  aircraft: Aircraft[];
  bookings: Booking[];      // live (non-cancelled) bookings in the window
  bookingKinds: BookingKind[];
  scopeIcaos: Set<string> | null; // null = all bases; else only these current_base_icao
  today: string;            // the board's VIEWED day (YYYY-MM-DD, anchor tz) — buckets follow the day on screen
  onPick: (b: Booking) => void;
}

export function MaintenanceSummary({ aircraft, bookings, bookingKinds, scopeIcaos, today, onPick }: MSProps) {
  const kindsById = new Map(bookingKinds.map((k) => [k.id, k]));
  // Active (unreturned) maintenance bookings, keyed by aircraft registration.
  const maintByReg = new Map<string, Booking>();
  for (const b of bookings) {
    const k = b.booking_kind_id ? kindsById.get(b.booking_kind_id) : null;
    if (!k?.is_maintenance || b.maintenance_returned_at) continue;
    const reg = b.asset_allocations.find((a) => a.aircraft_registration)?.aircraft_registration;
    if (reg && !maintByReg.has(reg)) maintByReg.set(reg, b);
  }

  const inScope = (a: Aircraft) => scopeIcaos === null || (!!a.current_base_icao && scopeIcaos.has(a.current_base_icao));

  const inMaint: { ac: Aircraft; booking: Booking; ecd: string | null }[] = [];
  const awaiting: { ac: Aircraft; booking: Booking; ecd: string | null }[] = [];
  const degraded: { ac: Aircraft; note: string }[] = [];

  for (const a of aircraft) {
    if (!inScope(a)) continue;
    if (a.serviceable === false) {
      // Item 2: a future-dated maintenance flips serviceable=false eagerly, but it hasn't
      // commenced — don't list it as "in maintenance" until its commencement date.
      if (a.unserviceable_from && a.unserviceable_from.slice(0, 10) > today) continue;
      const mb = maintByReg.get(a.registration);
      const ecdIso = mb?.arr_time_planned ?? a.expected_return_date ?? null;
      const ecd = ecdIso ? ecdIso.slice(0, 10) : null;
      if (mb) {
        if (ecd && ecd < today) awaiting.push({ ac: a, booking: mb, ecd });
        else inMaint.push({ ac: a, booking: mb, ecd });
      } else {
        degraded.push({ ac: a, note: a.status_note ? 'unserviceable' : 'unserviceable (no booking)' });
      }
    } else if (a.capability_current && a.capability_current !== a.capability_normal) {
      degraded.push({ ac: a, note: a.capability_current });
    }
  }

  if (inMaint.length === 0 && awaiting.length === 0 && degraded.length === 0) return null;

  const row = (reg: string, right: string, color: string, dot: string | null, title: string | undefined, onClick?: () => void) => (
    <button
      key={reg + right}
      title={title}
      onClick={onClick}
      disabled={!onClick}
      style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: 6, background: onClick ? C.amberLight : 'transparent', border: onClick ? `1px solid ${C.amberBorder}` : 'none', borderRadius: 5, padding: onClick ? '4px 7px' : '2px 1px', marginBottom: 4, cursor: onClick ? 'pointer' : 'default', textAlign: 'left' }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: C.text, fontFamily: M }}>
        {dot && <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot, display: 'inline-block' }} />}
        {reg}
      </span>
      <span style={{ fontSize: 9, color }}>{right}</span>
    </button>
  );

  const section = (label: string, color: string, children: React.ReactNode) => (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 9, fontWeight: 700, color, letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12 }}>
      {/* The title may wrap between words; the (date) is kept intact so it can never split mid-date
          ("(20" / "Jul)") in the narrow 224px right panel — worst case is a clean title / (date) break. */}
      <div style={{ fontSize: 11, fontWeight: 700, color: C.navy, letterSpacing: '0.08em', marginBottom: 10 }}>🔧 MAINTENANCE THIS DAY <span style={{ whiteSpace: 'nowrap' }}>({ddMMM(today)})</span></div>
      {inMaint.length > 0 && section('IN MAINTENANCE', C.accent, inMaint.map(({ ac, booking, ecd }) => {
        const dot = booking.booking_kind_id ? kindsById.get(booking.booking_kind_id)?.ui_color ?? C.accent : C.accent;
        const alloc = booking.asset_allocations.find((a) => a.aircraft_registration);
        // 0.12.7 Part D: the stored note is raw input; the sentence is rendered in one place.
        const note = (alloc ? renderMaintConfirmation(alloc, booking.dep_timezone) : null) ?? undefined;
        return row(ac.registration, ecd ? `ECD ${ddMMM(ecd)}` : 'UFN', C.textMuted, dot, note, () => onPick(booking));
      }))}
      {awaiting.length > 0 && section('AWAITING RETURN', C.amber, awaiting.map(({ ac, booking, ecd }) =>
        row(ac.registration, ecd ? `ECD ${ddMMM(ecd)} — confirm` : 'confirm', C.amber, C.amber, 'Past ECD — click to confirm returned', () => onPick(booking)),
      ))}
      {degraded.length > 0 && section('DEGRADED / U-S', C.purple, degraded.map(({ ac, note }) =>
        row(ac.registration, note, C.textMuted, C.purple, ac.status_note ?? undefined),
      ))}
    </div>
  );
}
